<!--
  CauldronRail — thin always-visible left rail with Home + cauldron icons.

  Clicking a cauldron icon navigates to that cauldron's `defaultPath`.
  The active cauldron's icon stays lit via `useActiveCauldron()` so the
  user always knows which "environment" they're in.

  Hover behaviour:
    - Hover-to-EXPAND of the whole shell is owned by `Sidebar.vue` (via
      `@mouseenter` on its outer aside).
    - Hover-to-PREVIEW of a different cauldron's contextual sidebar is
      done here: each rail button emits `@hover` / `@unhover`; the rail
      forwards those upward as `@hover-cauldron` (with the cauldron id,
      or `null` for Home) so the parent can swap the contextual sidebar
      contents without the user having to click.

  Cauldrons flagged with `placement: 'bottom'` (Platform) render in a
  bottom-anchored cluster so they sit at the rail's foot, matching the
  system-pane convention (VSCode/Notion settings).
-->
<template>
  <!--
    Right border separates rail from the contextual pane. When the rail
    is the only thing visible (collapsed-desktop) the outer shell's own
    border is already the visual right edge, so an extra rail border
    would render as a double line 8px in from the edge.
  -->
  <!--
    `w-16` matches the outer collapsed shell width (64px). When the rail
    is the only thing visible (rail-only mode), the icons land on the
    shell's true horizontal centre. In expanded mode the rail still
    occupies the leftmost 64px, leaving 192px for the contextual pane.
    Using a narrower rail (e.g. w-14) would centre the icons inside the
    rail itself but leave them off-centre relative to the shell — the
    user reads that as "right padding looks bigger than left".
  -->
  <aside
    :class="['cauldron-rail flex flex-col items-center py-3 gap-1 bg-theme-surface dark:bg-theme-bg h-full w-16 shrink-0', bordered ? 'border-r border-theme-border dark:border-theme-border-strong' : '']"
    aria-label="Primary"
  >
    <!-- Home is the universal anchor: always rendered, lit when the user
         is on `/` and no cauldron applies. Hover emits a `null` cauldron
         id so the parent reverts to showing the currently-active
         cauldron's contextual sidebar (Home itself has no contextual
         content). -->
    <rail-button
      to="/"
      :icon="mdiHome"
      :active="isHomeActive"
      :label="t('menu_home')"
      @hover="emit('hover-cauldron', null)"
      @unhover="emit('unhover-cauldron')"
    />

    <!-- News — direct rail entry. It's not a cauldron (no contextual
         sidebar pops up on hover) — clicking goes straight to /news.
         Sits between the Home divider and the cauldron group so it
         reads as "Home + News are quick top-level destinations; the
         cauldrons below are environments you drill into". -->
    <rail-button
      to="/news"
      :icon="mdiNewspaperVariantOutline"
      :active="isNewsActive"
      :label="t('menu_news')"
      @hover="emit('hover-cauldron', null)"
      @unhover="emit('unhover-cauldron')"
    />

    <!-- Top cauldrons — content surfaces (Data/Executive/
         Personalisation/Favourites). -->
    <rail-button
      v-for="cauldron in topCauldrons"
      :key="cauldron.id"
      :to="cauldron.defaultPath"
      :icon="cauldron.icon"
      :active="isActive(cauldron.id)"
      :disabled="demoEnabled && ['personalisation', 'favourites', 'platform'].includes(cauldron.id)"
      :label="t(cauldron.labelKey)"
      :tooltip="t(cauldron.tooltipKey)"
      @hover="emit('hover-cauldron', cauldron.id)"
      @unhover="emit('unhover-cauldron')"
    />

    <!-- Spacer pushes bottom-anchored cauldrons (Platform) to the rail's
         foot. Used by the system pane convention (settings at bottom). -->
    <div v-if="bottomCauldrons.length > 0" class="flex-1" aria-hidden="true" />
    <div
      v-if="bottomCauldrons.length > 0"
      class="mb-1 mt-2 w-7 border-t border-theme-border dark:border-theme-border-strong"
      aria-hidden="true"
    />
    <rail-button
      v-for="cauldron in bottomCauldrons"
      :key="cauldron.id"
      :to="cauldron.defaultPath"
      :icon="cauldron.icon"
      :active="isActive(cauldron.id)"
      :disabled="demoEnabled && ['personalisation', 'favourites', 'platform'].includes(cauldron.id)"
      :label="t(cauldron.labelKey)"
      :tooltip="t(cauldron.tooltipKey)"
      @hover="emit('hover-cauldron', cauldron.id)"
      @unhover="emit('unhover-cauldron')"
    />
  </aside>
</template>

<script setup>
  /* eslint-disable no-undef */
  import { demoEnabled } from '@/storage/demoMode';
  import { useActiveCauldron } from '@/composables/useActiveCauldron';
  import { mdiHome, mdiNewspaperVariantOutline } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import RailButton from './RailButton.vue';

  defineProps({
    /** Render the right divider. Off when the rail is the only visible
     *  pane (rail-only, collapsed-desktop) so we don't get a double
     *  border with the outer sidebar shell. */
    bordered: { type: Boolean, default: true },
  });
  // `hover-cauldron` carries the hovered cauldron's id (or `null` for
  // Home / empty rail-area). `unhover-cauldron` fires on per-button
  // mouseleave but the parent generally ignores it — clearing the
  // preview is driven by the shell-level `mouseleave`, so quick
  // mouse-throughs between buttons don't flicker.
  const emit = defineEmits(['hover-cauldron', 'unhover-cauldron']);

  const { t } = useI18n();
  const route = useRoute() || {};
  const { cauldrons, activeCauldronId, isActive } = useActiveCauldron();

  // Split cauldrons into "top" (rendered above the spacer) and "bottom"
  // (after the spacer, pushed to the rail's foot). Registry entries flagged
  // with `placement: 'bottom'` land in the bottom group.
  const topCauldrons = computed(() => cauldrons.filter((c) => c.placement !== 'bottom'));
  const bottomCauldrons = computed(() => cauldrons.filter((c) => c.placement === 'bottom'));

  const isHomeActive = computed(() => route.path === '/' && activeCauldronId.value === null);
  // News is a standalone rail entry (not a cauldron), so the active
  // detection is local — purely based on the route prefix.
  const isNewsActive = computed(() => (route.path || '').startsWith('/news'));
</script>
